/**
 * Shared React Query hook for fetching a browser session's metadata.json.
 *
 * Both {@link BrowserVideoView} (to learn the recording filename) and
 * {@link useMetadataSteps} (to read the steps array) call this hook with
 * the same `artifactsBase`. React Query's queryKey dedupes the request, so
 * a replay cold-load makes one HTTP fetch instead of two.
 *
 * 404 is modeled as data=null (not an error) because the executor may
 * still be finalizing the recording. The poll-while-finalizing loop lives
 * in the caller (BrowserVideoView), not in this hook — both `refetch()`
 * results land back in the same cache, so other consumers (useMetadataSteps)
 * see the eventual 200 result for free.
 *
 * @module browser-automation/hooks/use-recording-metadata
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '@/shared/auth';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';

/**
 * Thrown when the server has signaled "this session will never produce
 * a recording on this pod" — distinct from "still finalizing" so the
 * caller can short-circuit polling instead of waiting out the 3-min
 * ceiling. Two response shapes trigger this:
 *
 *  - **503** with `{"detail":"Artifacts service unavailable"}` — the
 *    new router's mount-detection branch (PVC not mounted on this pod).
 *  - **404** with `{"detail":"Not Found"}` — the FastAPI default for an
 *    unimplemented route, i.e. an old router predating PR #71.
 *
 * `404` with `{"detail":"Artifact not found"}` (the new router's
 * file-pending state) is **not** permanent — it stays as `data: null`
 * so the caller keeps polling.
 */
export class RecordingUnavailableError extends Error {
  readonly kind = 'recording-unavailable' as const;
  readonly reason: 'mount_missing' | 'endpoint_missing' | 'non_json_404';

  constructor(
    reason: 'mount_missing' | 'endpoint_missing' | 'non_json_404',
    message?: string,
  ) {
    super(message ?? reason);
    this.name = 'RecordingUnavailableError';
    this.reason = reason;
  }
}

export function isRecordingUnavailableError(
  err: unknown,
): err is RecordingUnavailableError {
  return (
    err instanceof Error &&
    (err as { kind?: string }).kind === 'recording-unavailable'
  );
}

/**
 * Shape of one step entry in metadata.json. Mirrors `_build_metadata_json`
 * in browser-farm-v2/executor/server.py. Extra fields are tolerated.
 */
export interface IRecordingMetadataStep {
  step_number: number;
  description: string;
  action_type: string;
  timestamp?: string;
  video_offset_seconds: number;
  duration_seconds: number;
  wall_duration_seconds?: number;
  frame_count?: number;
  success: boolean;
}

/**
 * Top-level metadata.json shape this app cares about. Loose typing on
 * inner fields so we don't have to chase executor schema additions.
 * `recording` is marked optional so callers must defensively check
 * `recording?.filename` — protects against zero-action sessions or
 * future schema changes that drop the field entirely.
 */
export interface IRecordingMetadata {
  session_id: string;
  task_description?: string | null;
  /**
   * False while the recording is still being written (incremental mid-run
   * publishes — e.g. a run paused for exception guidance), true once the
   * session is torn down and the recording is final. Absent on older
   * recordings, so treat `undefined` as "unknown / not finalized".
   */
  finalized?: boolean;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  recording?: {
    filename?: string;
    file_size_bytes?: number;
  };
  steps?: IRecordingMetadataStep[];
}

/**
 * Build the React Query key for a session's metadata. Exported so callers
 * (and tests) can manually invalidate or read the cache for the same key.
 */
export function recordingMetadataQueryKey(
  artifactsBase: string | null,
): readonly [string, string] {
  return ['browser-recording-metadata', artifactsBase ?? ''] as const;
}

/**
 * Fetch the session's metadata.json. Returns `null` on 404 (finalize
 * pending), the parsed metadata on 200, or throws on 5xx.
 *
 * Both `BrowserVideoView` and `useMetadataSteps` should call this hook
 * with the same `artifactsBase` — React Query dedupes the request.
 *
 * Polling on 404 is left to the caller via `refetch()`; this keeps the
 * hook stateless and predictable.
 */
export function useRecordingMetadata(
  artifactsBase: string | null,
): UseQueryResult<IRecordingMetadata | null, Error> {
  const { getAccessTokenSilently } = useAuth();
  return useQuery<IRecordingMetadata | null, Error>({
    queryKey: recordingMetadataQueryKey(artifactsBase),
    enabled: artifactsBase !== null,
    // Recording is immutable once finalized — never auto-refetch after a 200.
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // 5xx and network errors surface immediately — the caller decides whether
    // to retry. Polling on 404 (the only expected transient state) is driven
    // explicitly via refetch(), not via React Query's retry machinery.
    retry: false,
    queryFn: async ({ signal }) => {
      if (!artifactsBase) {
        // Guard: queryFn shouldn't run when disabled, but TS doesn't know.
        return null;
      }
      const headers: Record<string, string> = {};
      if (isE2ETestMode()) {
        headers['X-E2E-Test-Mode'] = 'true';
      } else {
        const token = await getAccessTokenSilently();
        headers.Authorization = `Bearer ${token}`;
      }
      const res = await fetch(`${artifactsBase}/metadata.json`, {
        headers,
        signal,
      });

      // Mount missing on this pod (new router, PVC not yet attached).
      // Permanent for the pod's lifetime — short-circuit polling.
      if (res.status === 503) {
        throw new RecordingUnavailableError(
          'mount_missing',
          'artifacts service unavailable',
        );
      }

      if (res.status === 404) {
        // Distinguish "endpoint doesn't exist" (FastAPI default body, old
        // router) from "file not yet written" (handler-emitted body, new
        // router still finalizing). The former is permanent for the pod
        // lifetime; the latter resolves once finalize completes.
        let detail: unknown = undefined;
        try {
          const body = (await res.clone().json()) as { detail?: unknown };
          detail = body?.detail;
        } catch {
          // Non-JSON 404 (HTML page, empty body, etc.) — treat as a
          // missing-endpoint shape. Old routers in some environments
          // may not return JSON for unhandled paths.
          throw new RecordingUnavailableError(
            'non_json_404',
            'artifacts endpoint not found (non-JSON 404)',
          );
        }
        if (detail === 'Not Found') {
          throw new RecordingUnavailableError(
            'endpoint_missing',
            'artifacts endpoint not implemented',
          );
        }
        // Any other 404 body (notably "Artifact not found") is transient.
        return null;
      }

      if (!res.ok) {
        throw new Error(`metadata fetch failed: ${res.status}`);
      }
      return (await res.json()) as IRecordingMetadata;
    },
  });
}
